/**
 * The landing page: a blacksmith's workshop. Three props are the three front doors:
 * the anvil forges a game (`/create`), the weapons rack is Play Now (`/play-now`),
 * the chest holds My Stuff. A door opens a hanging board over the dimmed room.
 *
 * `/create`, `/play-now` and `/signin` render this with their page as `children`, so each board
 * has a real address: links, refresh and Back all work. My Stuff is only links, so
 * it is local state. All the motion lives in `motion.ts`; this renders once.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IdentityBar } from '../components/IdentityBar';
import { BoardFrame } from './frame';
import { startMotion, type Motion } from './motion';
// The signs' lettering, bundled so an offline party still gets it and no visitor is sent to Google.
import '@fontsource/cinzel/latin-600.css';
import '@fontsource/cinzel/latin-700.css';
import room from '../assets/workshop/room.webp';
import anvil from '../assets/workshop/anvil.webp';
import hammer from '../assets/workshop/hammer.webp';
import rack from '../assets/workshop/rack.webp';
import chest from '../assets/workshop/chest.webp';

/** The first screen's art. The scene stays a dark room until it has all arrived. */
const FIRST_SCREEN = [room, anvil, hammer, rack, chest];

type BoardName = 'forge' | 'rack' | 'chest' | 'account';

const LABELS: Record<BoardName, string> = {
  forge: 'Forge a New Game',
  rack: 'Play Now',
  chest: 'My Stuff',
  account: 'Your Account',
};

/** The board's lift-away animation; closing waits for it before it unmounts. */
const CLOSE_MS = 380;
/** The desktop doors being reeled up into the title; `ws-reel` in styles.css. */
const REEL_MS = 1100;

interface Props {
  /** The board this route opens, with the route's page on it. */
  board?: 'forge' | 'rack' | 'account';
  children?: ReactNode;
}

export function Workshop({ board, children }: Props) {
  const navigate = useNavigate();
  const sceneRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const motion = useRef<Motion | null>(null);
  const [stuffOpen, setStuffOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [released, setReleased] = useState(false);
  const [ready, setReady] = useState(false);
  const open: BoardName | null = board ?? (stuffOpen ? 'chest' : null);

  // `navigate` can change identity between renders; the motion must not restart for it.
  const onImpact = useRef(() => navigate('/create'));
  onImpact.current = () => navigate('/create');

  // The scene animates only while it is on screen: a released board has covered it.
  useEffect(() => {
    const root = sceneRef.current;
    if (!root || released) return;
    const m = startMotion(root, () => onImpact.current());
    motion.current = m;
    return () => {
      m.stop();
      motion.current = null;
    };
  }, [released]);

  // A broken image still reveals the room: a missing prop beats a black page.
  useEffect(() => {
    let live = true;
    void Promise.all(
      FIRST_SCREEN.map((src) => {
        const img = new Image();
        img.src = src;
        return img.decode().catch(() => undefined);
      }),
    ).then(() => live && setReady(true));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    motion.current?.setBoardOpen(open !== null);
  }, [open, released]);

  // Released belongs to the board that outgrew the frame. Moving straight to another board
  // (Play Now's final score to sign-in) must hang the new one on the frame again.
  useEffect(() => setReleased(false), [board]);

  // On open, focus the board so it is announced and Escape reaches it. On close, hand
  // focus back to the prop that opened it.
  const lastOpen = useRef<BoardName | null>(null);
  useEffect(() => {
    if (open) {
      lastOpen.current = open;
      boardRef.current?.focus({ preventScroll: true });
      return;
    }
    setClosing(false);
    setReleased(false);
    const was = lastOpen.current;
    lastOpen.current = null;
    if (was) sceneRef.current?.querySelector<HTMLElement>(`[data-spot="${was}"]`)?.focus({ preventScroll: true });
  }, [open]);

  function close() {
    if (!open || closing || released) return;
    const finish = () => {
      if (board) navigate('/');
      else setStuffOpen(false);
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return finish();
    setClosing(true);
    window.setTimeout(finish, CLOSE_MS);
  }

  const frame = useMemo(() => ({ release: () => setReleased(true) }), []);

  return (
    <div className={`ws${open ? ' is-board' : ''}`}>
      <div className="ws-scene" ref={sceneRef} hidden={released} inert={open !== null} data-ready={ready || undefined}>
        <Scene
          open={open}
          onStrike={() => motion.current?.strike()}
          onChest={() => setStuffOpen(true)}
          onMenu={() => motion.current?.nudge()}
        />
      </div>

      {open && (
        <div
          className={released ? 'ws-page' : `ws-overlay${closing ? ' is-closing' : ''}`}
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') close();
            if (e.key === 'Tab' && !released) trapTab(e, boardRef.current);
          }}
        >
          <div
            ref={boardRef}
            className={released ? 'ws-page-inner' : 'ws-board'}
            role={released ? undefined : 'dialog'}
            aria-modal={released ? undefined : true}
            aria-label={released ? undefined : LABELS[open]}
            tabIndex={-1}
          >
            {/* A fixed slot, so releasing the board never remounts the page beside it. */}
            {!released && <BoardDressing onClose={close} />}
            <BoardFrame.Provider value={frame}>{board ? children : <MyStuff onClose={close} />}</BoardFrame.Provider>
          </div>
        </div>
      )}
    </div>
  );
}

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** The room is inert under a board but the identity bar above it isn't, so Tab wraps here. */
function trapTab(e: KeyboardEvent,board: HTMLElement | null) {
  if (!board) return;
  const all = [...board.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
  const first = all[0];
  const last = all.at(-1);
  if (!first || !last) return;
  const at = document.activeElement;
  if (e.shiftKey && (at === first || at === board)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && at === last) {
    e.preventDefault();
    first.focus();
  }
}

/** The painted room, its props, their signs, and the doors chained under the title. */
function Scene({
  open,
  onStrike,
  onChest,
  onMenu,
}: {
  open: BoardName | null;
  onStrike(): void;
  onChest(): void;
  /** The desktop chain has just dropped. */
  onMenu(): void;
}) {
  const [menu, setMenu] = useState<'open' | 'closing' | null>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const reel = useRef(0);
  useEffect(() => () => window.clearTimeout(reel.current), []);

  function openMenu() {
    window.clearTimeout(reel.current);
    onMenu();
    setMenu('open');
  }
  function closeMenu() {
    if (menu !== 'open') return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return setMenu(null);
    setMenu('closing');
    reel.current = window.setTimeout(() => setMenu(null), REEL_MS);
  }
  return (
    <>
      <div className="ws-stage" data-open={open ?? undefined}>
        <div className="ws-layer ws-room" data-depth="-18">
          <div className="ws-glow ws-glow-hearth" />
          <div className="ws-glow ws-glow-lamp is-left" />
          <div className="ws-glow ws-glow-lamp is-right" />
          <div className="ws-glow ws-glow-moon" />
          <canvas className="ws-fx" />
          <Link className="ws-prop ws-rack" to="/play-now" data-spot="rack" aria-label="Play Now">
            <span className="ws-prop-cast" />
            <span className="ws-prop-shadow" />
            <i className="ws-prop-img" />
            <i className="ws-prop-mask ws-prop-shade" />
            <i className="ws-prop-mask ws-prop-warm" />
            <i className="ws-prop-mask ws-prop-glint" />
          </Link>
          <button className="ws-prop ws-chest" type="button" data-spot="chest" aria-label="My Stuff" onClick={onChest}>
            <span className="ws-prop-shadow" />
            <i className="ws-prop-img" />
            <i className="ws-prop-img ws-chest-open" />
            <i className="ws-prop-mask ws-prop-shade" />
            <i className="ws-prop-mask ws-prop-moon" />
            <i className="ws-chest-light" />
          </button>
        </div>

        <div className="ws-layer" data-depth="-18">
          <Link
            className="ws-anvil"
            to="/create"
            data-spot="forge"
            aria-label="Forge a New Game"
            onClick={(e) => {
              // A plain click strikes first and opens on impact; a modified click is a link.
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              onStrike();
            }}
          >
            <span className="ws-anvil-cast" />
            <span className="ws-anvil-floor" />
            <span className="ws-anvil-tongs" />
            <i className="ws-anvil-img" />
            <i className="ws-anvil-mask ws-anvil-shade" />
            <i className="ws-anvil-mask ws-anvil-hearth" />
            <i className="ws-anvil-mask ws-anvil-shine" />
            <span className="ws-ingot" />
            <span className="ws-hammer-shadow" />
            <span className="ws-hammer" />
          </Link>
          <i className="ws-sign-anchor" data-sign="forge" style={{ left: '50%', top: '31%' }} />
          <i className="ws-sign-anchor" data-sign="rack" style={{ left: '18%', top: '36.5%' }} />
          <i className="ws-sign-anchor" data-sign="chest" style={{ left: '79.5%', top: '62%' }} />
        </div>
      </div>

      <div className="ws-gaze" />

      <header
        className="ws-masthead"
        data-menu={menu ?? undefined}
        onKeyDown={(e) => {
          if (e.key !== 'Escape' || menu !== 'open') return;
          closeMenu();
          toggle.current?.focus();
        }}
      >
        <h1 className="ws-brand">
          <small>Welcome to</small>
          <strong>
            GameShow<em>Forge</em>
          </strong>
        </h1>
        {/* Desktop only: phones always show the doors. */}
        <button
          ref={toggle}
          className="ws-menu-toggle"
          type="button"
          aria-label="Workshop menu"
          aria-expanded={menu === 'open'}
          aria-controls="ws-quick"
          onClick={() => (menu === 'open' ? closeMenu() : openMenu())}
        />
        {/* The props can sit off screen on a phone, so the three doors also hang from the title
            as plain buttons: side by side on a phone, one under the next on desktop. */}
        <nav className="ws-quick" id="ws-quick" aria-label="Workshop">
          <Link className="ws-quick-btn" to="/play-now">
            Play Now
          </Link>
          <Link className="ws-quick-btn" to="/create">
            Forge a Game
          </Link>
          <button className="ws-quick-btn" type="button" onClick={onChest}>
            My Stuff
          </button>
        </nav>
      </header>

      {/* The account hangs opposite the title, a plaque like the doors; it replaces the app's bar here. */}
      <IdentityBar variant="workshop" />

      {/* Above the gaze shading, so a sign is never dimmed; motion.ts pins each to its anchor. */}
      <div className="ws-signs" aria-hidden="true">
        <Sign name="forge" title="Forge a New Game" sub="Pick your rounds and host the show" />
        <Sign name="rack" title="Play Now" sub="A quick trivia game, no host needed" />
        <Sign name="chest" title="My Stuff" sub="Questions, templates, games and account" />
      </div>

      <p className="ws-hint">
        <span className="ws-hint-mouse">Look around the workshop. Click the anvil, the weapons rack or the chest.</span>
        <span className="ws-hint-touch">Drag to look around, or pick one above.</span>
      </p>
    </>
  );
}

function Sign({ name, title, sub }: { name: BoardName; title: string; sub: string }) {
  return (
    <div className={`ws-sign ws-sign-${name}`} data-sign={name}>
      <span className={`ws-medal ws-medal-${name}`} />
      <span className="ws-sign-text">
        <b>{title}</b>
        <span>{sub}</span>
      </span>
    </div>
  );
}

function BoardDressing({ onClose }: { onClose(): void }) {
  return (
    <>
      <span className="ws-board-foot" aria-hidden="true" />
      <button className="ws-board-close" type="button" aria-label="Close" onClick={onClose}>
        ✕
      </button>
    </>
  );
}

const ICONS = {
  plus: 'M10.5 4h3v6.5H20v3h-6.5V20h-3v-6.5H4v-3h6.5z',
  scroll:
    'M6 3h11a3 3 0 010 6h-1v10a3 3 0 01-3 3H5a3 3 0 010-6h1zm2 4v2h6V7zm0 4v2h6v-2zm0 4v2h4v-2z',
  stack: 'M12 3l9 4.5-9 4.5-9-4.5zm-9 9l9 4.5 9-4.5v3l-9 4.5-9-4.5z',
  die: 'M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2zm3 3.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm8 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm-4 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm-4 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm8 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z',
  gear: 'M10.4 2h3.2l.5 2.6 1.9.8 2.2-1.5 2.3 2.3-1.5 2.2.8 1.9 2.6.5v3.2l-2.6.5-.8 1.9 1.5 2.2-2.3 2.3-2.2-1.5-1.9.8-.5 2.6h-3.2l-.5-2.6-1.9-.8-2.2 1.5-2.3-2.3 1.5-2.2-.8-1.9L2 13.6v-3.2l2.6-.5.8-1.9-1.5-2.2 2.3-2.3 2.2 1.5 1.9-.8zM12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7z',
} as const;

const STUFF: { to: string; title: string; sub: string; icon: keyof typeof ICONS; primary?: boolean }[] = [
  { to: '/questions?new=1', title: 'Submit a New Question', sub: 'Add a clue to your reusable question bank.', icon: 'plus', primary: true },
  { to: '/questions', title: 'My Questions', sub: "Browse and edit the questions you've written.", icon: 'scroll' },
  // Templates live on the account screen; this is their full list.
  { to: '/admin/templates', title: 'My Templates', sub: 'Saved show formats you can run again.', icon: 'stack' },
  { to: '/games', title: 'My Games', sub: "Games you've hosted, and how you did in the ones you played.", icon: 'die' },
  { to: '/admin', title: 'Account Settings', sub: 'Your profile and password.', icon: 'gear' },
];

/** The chest: links to everything a host keeps. Past games live on My Games, not here. */
function MyStuff({ onClose }: { onClose(): void }) {
  return (
    <>
      <h1 className="ws-board-title">My Stuff</h1>
      <p className="ws-board-sub">Everything you&rsquo;ve made, kept in the chest.</p>
      <nav className="ws-planks" aria-label="My Stuff">
        {STUFF.map((item) => (
          <Link key={item.title} className={`ws-plank${item.primary ? ' is-primary' : ''}`} to={item.to}>
            <span className="ws-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d={ICONS[item.icon]} />
              </svg>
            </span>
            <span className="ws-plank-text">
              <strong>{item.title}</strong>
              <small>{item.sub}</small>
            </span>
          </Link>
        ))}
      </nav>
      <button className="btn btn-ghost ws-board-back" type="button" onClick={onClose}>
        ← Back to the workshop
      </button>
    </>
  );
}
